import { Card, Col, Row, Skeleton, Typography } from 'antd';
import { Notice } from '../common/Notice';
import {
  QuantitativeSummaryDistributionItem,
  QuantitativeSummaryResponse,
} from '../../types/audit-trail.types';

const { Text } = Typography;

// ============================================================================
// Horizontal bar chart (single distribution)
// ============================================================================

const BAR_COLORS = [
  '#2E2B81',
  '#5856D6',
  '#34C759',
  '#FF9500',
  '#FF3B30',
  '#007AFF',
  '#AF52DE',
  '#FF2D55',
];


interface DistributionChartProps {
  title: string;
  items: QuantitativeSummaryDistributionItem[];
  alwaysShowSingleCountInsideBar?: boolean;
}

function DistributionChart({
  title,
  items,
  alwaysShowSingleCountInsideBar = false,
}: DistributionChartProps) {
  const maxCount = Math.max(...items.map((i) => i.count), 1);

  return (
    <Card size="small" title={title} style={{ height: '100%' }}>
      {items.length === 0 ? (
        <Text type="secondary">Sem dados</Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item, idx) => {
            const barWidth = (item.count / maxCount) * 100;
            const color = BAR_COLORS[idx % BAR_COLORS.length];
            const shouldForceSingleCountLabel =
              alwaysShowSingleCountInsideBar && item.count === 1;
            const shouldShowCountInsideBar = barWidth > 25 || shouldForceSingleCountLabel;

            return (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 120, flexShrink: 0, overflow: 'hidden' }}>
                  <Text
                    style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={item.label}
                  >
                    {item.label}
                  </Text>
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      background: '#f0f0f0',
                      borderRadius: 4,
                      height: 24,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        background: color,
                        height: '100%',
                        width: `${barWidth}%`,
                        borderRadius: 4,
                        transition: 'width 0.4s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: 6,
                        minWidth: barWidth > 0 ? (shouldForceSingleCountLabel ? 22 : 4) : 0,
                      }}
                    >
                      {shouldShowCountInsideBar && (
                        <Text style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                          {item.count}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ width: 60, textAlign: 'right', flexShrink: 0 }}>
                  <Text style={{ fontSize: 12 }}>
                    {item.count}{' '}
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ({item.percentage}%)
                    </Text>
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// Main component
// ============================================================================

interface Props {
  data: QuantitativeSummaryResponse | null;
  loading: boolean;
}

export default function PublicAuditTrailQuantitativeSummary({ data, loading }: Props) {
  if (loading) {
    return (
      <Card title="Perfil dos Participantes" style={{ marginBottom: 16 }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  if (!data) return null;

  // State: no summary calculated yet
  if (!data.available && !data.belowThreshold) {
    return (
      <Card title="Perfil dos Participantes" style={{ marginBottom: 16 }}>
        <Notice
          type="info"
          showIcon
          message="Os indicadores de perfil estão sendo processados."
          description="Os dados serão exibidos em alguns minutos após o primeiro ciclo de cálculo."
        />
      </Card>
    );
  }

  // State: below minimum threshold
  if (!data.available && data.belowThreshold) {
    const missing = data.thresholdRequired - data.totalResponses;
    return (
      <Card title="Perfil dos Participantes" style={{ marginBottom: 16 }}>
        <Notice
          type="info"
          showIcon
          message={`Aguardando participações para exibir os indicadores de perfil.`}
          description={`São necessárias pelo menos ${data.thresholdRequired} respostas. Faltam ${missing}.`}
        />
      </Card>
    );
  }

  // State: available with distributions
  const { distributions, calculatedAt } = data;
  if (!distributions) return null;

  const formattedAt = calculatedAt
    ? new Date(calculatedAt).toLocaleString('pt-BR')
    : null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <DistributionChart title="Gênero" items={distributions.gender} />
        </Col>
        <Col xs={24} sm={12}>
          <DistributionChart title="Faixa Etária" items={distributions.ageRange} />
        </Col>
        <Col xs={24} sm={12}>
          <DistributionChart
            title="Escolaridade"
            items={distributions.education}
            alwaysShowSingleCountInsideBar
          />
        </Col>
        <Col xs={24} sm={12}>
          <DistributionChart
            title="Classe Econômica (Salário Mínimo)"
            items={distributions.economic}
            alwaysShowSingleCountInsideBar
          />
        </Col>
        <Col xs={24} sm={12}>
          <DistributionChart
            title="Estado"
            items={distributions.state}
            alwaysShowSingleCountInsideBar
          />
        </Col>
        <Col xs={24} sm={12}>
          <DistributionChart
            title="Cidade"
            items={distributions.city}
            alwaysShowSingleCountInsideBar
          />
        </Col>
      </Row>
      <Text type="secondary" style={{ fontSize: 12 }}>
            Atualizado em {formattedAt}
      </Text>
    </div>
  );
}
